<template>
  <span
    v-if="isVisible"
    class="badge"
    :class="[`badge--${color}`, { 'badge--dot': dot, 'badge--single': isSingleCharacterBadge }]"
    role="status"
    aria-live="polite"
  >
    <template v-if="!dot">{{ renderedValue }}</template>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type AppNotificationBadgeColor = 'red' | 'brand'

const MAX_BADGE_VALUE = 99

const props = withDefaults(
  defineProps<{
    count?: number
    dot?: boolean
    color?: AppNotificationBadgeColor
  }>(),
  {
    dot: false,
    color: 'red',
  },
)

const isVisible = computed(() => props.dot || (props.count !== undefined && props.count > 0))

const renderedValue = computed(() =>
  props.count !== undefined && props.count > MAX_BADGE_VALUE ? `${MAX_BADGE_VALUE}+` : String(props.count ?? ''),
)

const isSingleCharacterBadge = computed(() => renderedValue.value.length === 1)
</script>

<style scoped lang="scss">
.badge {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  pointer-events: none;
  z-index: 1;

  min-width: 20px;
  height: 20px;
  padding: 4px 6px;
  border-radius: 10px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 12px;
  line-height: 1;
  color: #ffffff;
  white-space: nowrap;
  box-sizing: border-box;
}

.badge--single {
  width: 20px;
  padding: 0;
}

.badge--dot {
  min-width: 12px;
  width: 12px;
  height: 12px;
  padding: 0;
}

.badge--red {
  background-color: var(--color-danger-emphasis);
}

.badge--brand {
  background-color: var(--color-brand-default);
}
</style>
